import { collectAll } from "@/components/table/export";
import {
  ORDER_CHART_FIELDS,
  RETURN_LOSS_FIELDS,
} from "@/modules/reports/fields";
import { fetchAllPages } from "@/modules/reports/lib/pagination";
import { projectReturnLossRow } from "@/modules/reports/returns";
import { Delivery } from "@/modules/reports/screens/Delivery";
import { Gst } from "@/modules/reports/screens/Gst";
import { Inventory } from "@/modules/reports/screens/Inventory";
import { Orders } from "@/modules/reports/screens/Orders";
import { Products } from "@/modules/reports/screens/Products";
import { ReturnsReport } from "@/modules/reports/screens/ReturnsReport";
import { Sales } from "@/modules/reports/screens/Sales";
import type { ModuleUi } from "@/modules/types";

/**
 * The Reports module (ticket #96).
 *
 * Sales, Products, Inventory and GST are aggregations over other modules'
 * rows, not filterable lists of a row type this module owns — none declares
 * fields, the same way GST's `hsn-summary` and `exports` sections do, and the
 * toolbar's search, filter and column controls sit out on all four.
 *
 * Orders (#449) is the exception: a chart over the orders list's own filter,
 * so it declares the order fields and gets the shared filter builder — and,
 * being a chart, none of the column chooser or the sort.
 */
export const reportsModule: ModuleUi = {
  id: "reports",
  sections: {
    sales: { fields: [], screen: Sales, padded: true },
    orders: {
      fields: ORDER_CHART_FIELDS,
      subject: "orders",
      report: true,
      /*
        The product filter's options are the catalogue's products (#449), by
        title — the same choice the collection filter makes (#308), and for the
        same reason: it is what a link or a saved view reads back. All of them,
        sold or not; a product with no orders is a filter that matches nothing,
        which is an answer. Without catalogue access the field has no options
        and takes a typed title, which filters the same.
      */
      fieldOptions: async (data) => {
        const products = await fetchAllPages((query) =>
          data.catalog.listProducts(query),
        );
        const titles = [...new Set(products.map((product) => product.title))];
        titles.sort((a, b) => a.localeCompare(b));
        return {
          product: titles.map((title) => ({ value: title, label: title })),
        };
      },
      screen: Orders,
      padded: true,
    },
    /*
      Delivery and speed (#528): RTO rate, delivery success, first-attempt rate
      and the two medians, as a total and split by courier, state and payment.
      An aggregate, not a list, so it declares no fields.
    */
    delivery: { fields: [], screen: Delivery, padded: true },
    products: { fields: [], screen: Products, padded: true },
    inventory: { fields: [], screen: Inventory, padded: true },
    gst: { fields: [], screen: Gst, padded: true },
    /*
      Damaged and missing returns (#529): a list, over the rows of the receipts
      the warehouse made, through the shared toolbar.
    */
    returns: {
      fields: RETURN_LOSS_FIELDS,
      subject: "damaged and missing returns",
      screen: ReturnsReport,
      exportRows: (data, query) =>
        collectAll(
          (page) => data.returns.listLosses(page),
          query,
          projectReturnLossRow,
        ),
    },
  },
};
